"use client";

import React from "react";
import { motion, useAnimation } from "framer-motion";
import SectionLayout from "../ui/SectionLayout";
import { SectionProps } from "./model";
import { getDefaultCmsSection } from "@/utils/helper";

const HighlightSectionComponent: React.FC<
  SectionProps<{
    highlights: {
      imageUrl: string;
      title: string;
      description: string;
      link: string;
    }[];
  }>
> = ({ cmsSection }) => {
  const controls = useAnimation();

  return (
    <SectionLayout
      cmsSection={getDefaultCmsSection(cmsSection, {
        width: "xl",
      })}
    >
      <div className="flex flex-col lg:grid lg:grid-cols-3 justify-center w-full gap-4">
        {cmsSection.specifics.highlights.map((highlight, index) => (
          <motion.section
            key={index}
            initial="initial"
            whileInView="animate"
            animate={controls}
            viewport={{
              once: true,
            }}
            custom={index}
            variants={{
              initial: {
                opacity: 0,
                y: 100,
              },
              animate: (index: number) => ({
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.05 * index, // Apply delay only for the first 20 items
                },
              }),
            }}
            className="h-full w-full bg-[var(--hightlight-bg-color)] text-[var(--hightlight-text-color)] flex justify-center items-center"
          >
            <Wrapper link={highlight.link}>
              <div className="size-[220px]">
                <img
                  className="h-[220px] w-[220px]"
                  alt={`highlight ${index}`}
                  src={highlight.imageUrl}
                />
              </div>
              <div className="homepage-text-xl">{highlight.title}</div>
              <div className="text-center homepage-text-sm">
                {highlight.description}
              </div>
            </Wrapper>
          </motion.section>
        ))}
      </div>
    </SectionLayout>
  );
};

export default HighlightSectionComponent;

const Wrapper = ({
  children,
  link,
}: {
  children: React.ReactNode;
  link?: string;
}) => {
  if (link) {
    return (
      <a href={link} className="flex w-full flex-col items-center p-10 gap-2">
        {children}
      </a>
    );
  }
  return (
    <div className="flex w-full flex-col items-center p-10 gap-2">
      {children}
    </div>
  );
};
